import type {
	GeoJSONLineString,
	GeoJSONPolygon,
	ZoneBoundary,
} from "@sitescene/types";
import type { Feature, LineString, Polygon, Position } from "geojson";

import {
	isPointInGeoJsonPolygon,
	isSimpleGeoJsonPolygon,
} from "./geojson-polygon";

/**
 * Minimum span (~25 m at mid-latitudes) so a lone / collocated sensor still
 * shows a visible shape in editors — small enough not to swallow neighboring
 * zone pills on the dashboard map when an auto fill is drawn.
 */
const DEFAULT_MIN_SPAN_DEGREES = 0.00022;

/** Extra padding around computed bounds as a fraction of span. */
const DEFAULT_PADDING_RATIO = 0.001;

export type ZoneBoundsOptions = {
	minSpanDegrees?: number;
	paddingRatio?: number;
	shape?: ZoneBoundsShape;
};

/** Derived zone boundary algorithms. */
export type ZoneBoundsShape = "bbox" | "convexHull" | "markerConnect";

export const DEFAULT_ZONE_BOUNDS_SHAPE: ZoneBoundsShape = "markerConnect";

export type BuildZoneBoundsParams = {
	coordinates: [number, number][];
	/**
	 * When false, skip the auto hull if there is no usable stored polygon.
	 * Manual mode uses this so a cleared outline does not snap back to Auto.
	 */
	fallbackToDerived?: boolean;
	options?: ZoneBoundsOptions;
	/** Saved manual boundary — takes precedence over derived shapes */
	storedBoundary?: null | ZoneBoundary;
};

const getPointsCentroid = (points: [number, number][]): [number, number] => {
	const totalLng = points.reduce((sum, [lng]) => sum + lng, 0);
	const totalLat = points.reduce((sum, [, lat]) => sum + lat, 0);
	return [totalLng / points.length, totalLat / points.length];
};

export const geoJsonPolygonToFeature = (
	polygon: GeoJSONPolygon,
): Feature<Polygon> => ({
	geometry: polygon,
	properties: {},
	type: "Feature",
});

export const geoJsonLineStringToFeature = (
	line: GeoJSONLineString,
): Feature<LineString> => ({
	geometry: line,
	properties: {},
	type: "Feature",
});

const NUMERIC_KEY = /^\d+$/;

const toIndexArray = (value: unknown): null | unknown[] => {
	if (Array.isArray(value)) return value;
	if (!value || typeof value !== "object") return null;

	const keys = Object.keys(value)
		.filter((key) => NUMERIC_KEY.test(key))
		.sort((left, right) => Number(left) - Number(right));

	if (keys.length === 0) return null;
	if (keys.some((key, index) => Number(key) !== index)) return null;

	return keys.map((key) => (value as Record<string, unknown>)[key]);
};

const coerceNestedArrays = (value: unknown): unknown => {
	const asArray = toIndexArray(value);
	if (!asArray) return value;
	return asArray.map((item) => coerceNestedArrays(item));
};

const toFiniteNumber = (value: unknown): null | number => {
	if (typeof value === "number" && Number.isFinite(value)) return value;
	if (typeof value === "string" && value.trim() !== "") {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	}
	if (Array.isArray(value) && value.length === 1) {
		return toFiniteNumber(value[0]);
	}
	return null;
};

const toGeoJsonPosition = (value: unknown): [number, number] | null => {
	if (Array.isArray(value)) {
		if (value.length === 1) return toGeoJsonPosition(value[0]);
		if (value.length !== 2 && value.length !== 3) return null;

		const lng = toFiniteNumber(value[0]);
		const lat = toFiniteNumber(value[1]);
		if (lng !== null && lat !== null) return [lng, lat];
		return null;
	}

	if (value && typeof value === "object") {
		const candidate = value as {
			0?: unknown;
			1?: unknown;
			coordinates?: unknown;
			lat?: unknown;
			latitude?: unknown;
			lng?: unknown;
			longitude?: unknown;
		};

		if (candidate.coordinates !== undefined) {
			return toGeoJsonPosition(candidate.coordinates);
		}

		const namedLng = toFiniteNumber(candidate.lng ?? candidate.longitude);
		const namedLat = toFiniteNumber(candidate.lat ?? candidate.latitude);
		if (namedLng !== null && namedLat !== null) return [namedLng, namedLat];

		const indexLng = toFiniteNumber(candidate[0]);
		const indexLat = toFiniteNumber(candidate[1]);
		if (indexLng !== null && indexLat !== null) return [indexLng, indexLat];
	}

	return null;
};

const closeRing = (ring: [number, number][]): [number, number][] => {
	if (ring.length === 0) return ring;
	const first = ring[0]!;
	const last = ring[ring.length - 1]!;
	if (first[0] === last[0] && first[1] === last[1]) return ring;
	return [...ring, first];
};

const collectNumbersDeep = (value: unknown): number[] => {
	if (typeof value === "number" && Number.isFinite(value)) return [value];

	if (typeof value === "string" && value.trim() !== "") {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? [parsed] : [];
	}

	const asArray = toIndexArray(value);
	if (asArray) {
		return asArray.flatMap((item) => collectNumbersDeep(item));
	}

	if (value && typeof value === "object") {
		const position = toGeoJsonPosition(value);
		if (position) return [...position];
	}

	return [];
};

const pairNumbersToRing = (numbers: number[]): [number, number][] | null => {
	if (numbers.length < 6 || numbers.length % 2 !== 0) return null;

	const ring: [number, number][] = [];
	for (let index = 0; index < numbers.length; index += 2) {
		ring.push([numbers[index]!, numbers[index + 1]!]);
	}

	return closeRing(ring);
};

const toPlainRing = (ring: unknown): [number, number][] | null => {
	const values = toIndexArray(ring);
	if (!values) return null;

	const positions: [number, number][] = [];
	for (const value of values) {
		const position = toGeoJsonPosition(value);
		if (!position) return null;
		positions.push(position);
	}

	if (positions.length < 3) return null;
	return closeRing(positions);
};

const normalizeCoordinatesToRings = (
	coordinates: unknown,
): [number, number][][] | null => {
	const coerced = coerceNestedArrays(coordinates);
	if (!Array.isArray(coerced) || coerced.length === 0) return null;

	let coords = coerced;

	for (let depth = 0; depth < 6; depth += 1) {
		const first = coords[0];
		const firstPosition = toGeoJsonPosition(first);
		if (firstPosition) {
			const ring = toPlainRing(coords);
			if (ring) return [ring];
			break;
		}

		if (Array.isArray(first) && toGeoJsonPosition(first[0])) {
			const rings: [number, number][][] = [];
			for (const ring of coords) {
				const plainRing = toPlainRing(ring);
				if (!plainRing) {
					rings.length = 0;
					break;
				}
				rings.push(plainRing);
			}
			if (rings.length > 0) return rings;
		}

		if (coords.length === 1 && Array.isArray(first)) {
			coords = first;
			continue;
		}

		break;
	}

	const flattened = pairNumbersToRing(collectNumbersDeep(coordinates));
	return flattened ? [flattened] : null;
};

const coerceToGeoJsonPolygon = (input: unknown): GeoJSONPolygon | null => {
	if (input == null) return null;

	if (Array.isArray(input)) {
		return {
			coordinates: input as GeoJSONPolygon["coordinates"],
			type: "Polygon",
		};
	}

	if (typeof input !== "object") return null;

	const candidate = input as { coordinates?: unknown; type?: unknown };
	if (candidate.type === "LineString") {
		return null;
	}

	if (candidate.type === "Polygon" && Array.isArray(candidate.coordinates)) {
		return input as GeoJSONPolygon;
	}

	if (Array.isArray(candidate.coordinates)) {
		return {
			coordinates: candidate.coordinates as GeoJSONPolygon["coordinates"],
			type: "Polygon",
		};
	}

	return null;
};

/**
 * Coerces boundary coordinates into RFC 7946 Polygon rings.
 * Mapbox Draw expects [[[lng, lat], ...]] and throws if a ring is not an array.
 */
export const normalizeGeoJsonPolygon = (
	input: unknown,
): GeoJSONPolygon | null => {
	const polygon = coerceToGeoJsonPolygon(input);
	if (!polygon || !Array.isArray(polygon.coordinates)) {
		return null;
	}

	if (polygon.coordinates.length === 0) {
		return null;
	}

	const rings = normalizeCoordinatesToRings(polygon.coordinates);
	if (!rings) {
		return null;
	}

	return { coordinates: rings, type: "Polygon" };
};

const pairNumbersToPositions = (
	numbers: number[],
): [number, number][] | null => {
	if (numbers.length < 4 || numbers.length % 2 !== 0) return null;

	const positions: [number, number][] = [];
	for (let index = 0; index < numbers.length; index += 2) {
		positions.push([numbers[index]!, numbers[index + 1]!]);
	}

	return positions;
};

/**
 * Unwraps Mixed / mongoose / altitude-bearing LineString vertices into
 * RFC 7946 `[lng, lat]` pairs. Polygon rings already go through this path;
 * LineString used to keep only plain arrays, so a refresh fell back to the
 * short camera-connect polyline.
 */
const toPlainLineCoordinates = (value: unknown): [number, number][] | null => {
	const asArray = toIndexArray(coerceNestedArrays(value));
	if (!asArray || asArray.length === 0) return null;

	const fromPositions = asArray
		.map((item) => toGeoJsonPosition(item))
		.filter((item): item is [number, number] => item !== null);

	if (fromPositions.length === asArray.length && fromPositions.length >= 2) {
		return fromPositions;
	}

	if (asArray.length === 1) {
		const nested = toPlainLineCoordinates(asArray[0]);
		if (nested) return nested;
	}

	if (fromPositions.length >= 2) return fromPositions;

	return pairNumbersToPositions(collectNumbersDeep(asArray));
};

export const normalizeGeoJsonLineString = (
	input: unknown,
): GeoJSONLineString | null => {
	if (input == null || typeof input !== "object" || Array.isArray(input)) {
		return null;
	}

	const candidate = input as { coordinates?: unknown; type?: unknown };
	if (candidate.type !== "LineString") {
		return null;
	}

	const coordinates = toPlainLineCoordinates(candidate.coordinates);
	if (!coordinates || coordinates.length < 2) return null;

	const first = coordinates[0];
	const last = coordinates[coordinates.length - 1];
	const open =
		first &&
		last &&
		first[0] === last[0] &&
		first[1] === last[1] &&
		coordinates.length > 2
			? coordinates.slice(0, -1)
			: coordinates;

	if (open.length < 2) return null;

	return { coordinates: open, type: "LineString" };
};

export const normalizeZoneBoundary = (input: unknown): null | ZoneBoundary => {
	const line = normalizeGeoJsonLineString(input);
	if (line) return line;
	return normalizeGeoJsonPolygon(input);
};

/** Open LineString from a polygon ring (drops a closing vertex). */
export const geoJsonPolygonToOpenLineString = (
	polygon: GeoJSONPolygon,
): GeoJSONLineString | null => {
	const ring = polygon.coordinates[0];
	if (!ring || ring.length < 2) return null;

	const first = ring[0];
	const last = ring[ring.length - 1];
	const open =
		first && last && first[0] === last[0] && first[1] === last[1]
			? ring.slice(0, -1)
			: ring;
	if (open.length < 2) return null;

	return normalizeGeoJsonLineString({
		coordinates: open,
		type: "LineString",
	});
};

/** Prefer a stored LineString; otherwise open a stored Polygon. */
export const storedBoundaryToLineString = (
	boundary: unknown,
): GeoJSONLineString | null => {
	const line = normalizeGeoJsonLineString(boundary);
	if (line) return line;

	const polygon = normalizeGeoJsonPolygon(boundary);
	if (!polygon) return null;

	return geoJsonPolygonToOpenLineString(polygon);
};

export const featureToGeoJsonLineString = (
	feature: Feature<LineString>,
): GeoJSONLineString | null =>
	normalizeGeoJsonLineString({
		coordinates: feature.geometry.coordinates,
		type: "LineString",
	});

export const featureToGeoJsonPolygon = (
	feature: Feature<Polygon>,
): GeoJSONPolygon | null =>
	normalizeGeoJsonPolygon({
		coordinates: feature.geometry.coordinates as GeoJSONPolygon["coordinates"],
		type: "Polygon",
	});

/**
 * Connects each marker with straight lines in angular order around the centroid.
 * Unlike a convex hull, every marker is visited and interior points stay inside
 * the polygon rather than being wrapped by an outer shell.
 */
export const coordinatesToMarkerConnectPolygon = (
	coordinates: [number, number][],
	options: ZoneBoundsOptions = {},
): Feature<Polygon> | null => {
	if (coordinates.length === 0) return null;

	const uniquePoints = dedupeCoordinates(coordinates);
	if (uniquePoints.length < 3) {
		return coordinatesToBoundingBoxPolygon(coordinates, options);
	}

	const centroid = getPointsCentroid(uniquePoints);
	const sorted = [...uniquePoints].sort((left, right) => {
		const leftAngle = Math.atan2(left[1] - centroid[1], left[0] - centroid[0]);
		const rightAngle = Math.atan2(
			right[1] - centroid[1],
			right[0] - centroid[0],
		);
		return leftAngle - rightAngle;
	});

	return toPolygonFeature(closeRing(sorted));
};

const sortCoordinatesAroundCentroid = (
	coordinates: [number, number][],
): [number, number][] => {
	const uniquePoints = dedupeCoordinates(coordinates);
	if (uniquePoints.length < 2) return uniquePoints;

	const centroid = getPointsCentroid(uniquePoints);
	return [...uniquePoints].sort((left, right) => {
		const leftAngle = Math.atan2(left[1] - centroid[1], left[0] - centroid[0]);
		const rightAngle = Math.atan2(
			right[1] - centroid[1],
			right[0] - centroid[0],
		);
		return leftAngle - rightAngle;
	});
};

/**
 * Open polyline visiting each marker in angular order. Does not close back to
 * the first point — Perimeter is a border, not a filled ring.
 */
export const coordinatesToMarkerConnectLineString = (
	coordinates: [number, number][],
): GeoJSONLineString | null => {
	const sorted = sortCoordinatesAroundCentroid(coordinates);
	if (sorted.length < 2) return null;

	return { coordinates: sorted, type: "LineString" };
};

/**
 * Custom line overlays must not fall back to connecting cameras — that
 * replaces a long drawn path with a short V through the zone's sensors.
 */
export const resolveLineZoneOverlay = (
	storedBoundary: unknown,
	markerCoordinates: [number, number][],
	liveLine?: GeoJSONLineString,
): GeoJSONLineString | null => {
	if (liveLine) return liveLine;

	const storedLine = storedBoundaryToLineString(storedBoundary);
	if (storedLine) return storedLine;
	if (storedBoundary != null) return null;

	return coordinatesToMarkerConnectLineString(markerCoordinates);
};

const cross = (
	origin: [number, number],
	a: [number, number],
	b: [number, number],
): number =>
	(a[0] - origin[0]) * (b[1] - origin[1]) -
	(a[1] - origin[1]) * (b[0] - origin[0]);

const dedupeCoordinates = (
	coordinates: [number, number][],
): [number, number][] => {
	const seen = new Set<string>();
	const unique: [number, number][] = [];

	for (const coordinate of coordinates) {
		const key = coordinate.join(",");
		if (seen.has(key)) continue;
		seen.add(key);
		unique.push(coordinate);
	}

	return unique;
};

const getRingCentroid = (ring: [number, number][]): [number, number] => {
	if (!Array.isArray(ring) || ring.length === 0) {
		return [0, 0];
	}

	const vertices = ring.slice(0, -1);
	const totalLng = vertices.reduce((sum, [lng]) => sum + lng, 0);
	const totalLat = vertices.reduce((sum, [, lat]) => sum + lat, 0);
	return [totalLng / vertices.length, totalLat / vertices.length];
};

const expandRingFromCentroid = (
	ring: [number, number][],
	paddingRatio: number,
): [number, number][] => {
	if (ring.length < 2) return ring;

	const centroid = getRingCentroid(ring);
	const padFactor = 1 + paddingRatio;
	const expanded = ring
		.slice(0, -1)
		.map(
			([lng, lat]) =>
				[
					centroid[0] + (lng - centroid[0]) * padFactor,
					centroid[1] + (lat - centroid[1]) * padFactor,
				] as [number, number],
		);

	return [...expanded, expanded[0]!];
};

/** Extra padding when growing a manual outline to cover outside sensors. */
const INCLUDE_POINTS_PADDING_RATIO = 0.02;
/** ~15m — push a new vertex past the sensor so it is strictly inside. */
const INCLUDE_POINT_OFFSET_DEGREES = 0.00015;

const distanceToSegmentSquared = (
	point: [number, number],
	start: [number, number],
	end: [number, number],
): number => {
	const deltaLng = end[0] - start[0];
	const deltaLat = end[1] - start[1];
	if (deltaLng === 0 && deltaLat === 0) {
		const offsetLng = point[0] - start[0];
		const offsetLat = point[1] - start[1];
		return offsetLng * offsetLng + offsetLat * offsetLat;
	}

	const lengthSquared = deltaLng * deltaLng + deltaLat * deltaLat;
	const projection =
		((point[0] - start[0]) * deltaLng + (point[1] - start[1]) * deltaLat) /
		lengthSquared;
	const clamped = Math.max(0, Math.min(1, projection));
	const closestLng = start[0] + clamped * deltaLng;
	const closestLat = start[1] + clamped * deltaLat;
	const offsetLng = point[0] - closestLng;
	const offsetLat = point[1] - closestLat;
	return offsetLng * offsetLng + offsetLat * offsetLat;
};

const insertPointBeyondNearestEdge = (
	ring: [number, number][],
	point: [number, number],
): [number, number][] => {
	if (ring.length < 2) return [...ring, point];

	let nearestIndex = 0;
	let nearestDistance = Number.POSITIVE_INFINITY;

	for (let index = 0; index < ring.length; index += 1) {
		const start = ring[index]!;
		const end = ring[(index + 1) % ring.length]!;
		const distance = distanceToSegmentSquared(point, start, end);
		if (distance < nearestDistance) {
			nearestDistance = distance;
			nearestIndex = index;
		}
	}

	const start = ring[nearestIndex]!;
	const end = ring[(nearestIndex + 1) % ring.length]!;
	const centroid = getRingCentroid(closeRing(ring));
	const edgeLng = end[0] - start[0];
	const edgeLat = end[1] - start[1];
	let normalLng = -edgeLat;
	let normalLat = edgeLng;
	const normalLength = Math.hypot(normalLng, normalLat) || 1;
	normalLng /= normalLength;
	normalLat /= normalLength;

	const midpoint: [number, number] = [
		(start[0] + end[0]) / 2,
		(start[1] + end[1]) / 2,
	];
	const towardCentroidLng = centroid[0] - midpoint[0];
	const towardCentroidLat = centroid[1] - midpoint[1];
	if (normalLng * towardCentroidLng + normalLat * towardCentroidLat > 0) {
		normalLng = -normalLng;
		normalLat = -normalLat;
	}

	const inserted: [number, number] = [
		point[0] + normalLng * INCLUDE_POINT_OFFSET_DEGREES,
		point[1] + normalLat * INCLUDE_POINT_OFFSET_DEGREES,
	];

	const next = [...ring];
	next.splice(nearestIndex + 1, 0, inserted);
	return next;
};

const polygonContainsAllPoints = (
	polygon: GeoJSONPolygon,
	points: [number, number][],
): boolean => points.every((point) => isPointInGeoJsonPolygon(point, polygon));

/**
 * Grows a polygon so the given points lie inside it, keeping the existing
 * outline where possible. Falls back to a padded convex hull if inserting
 * vertices would make the ring cross itself or still miss a point.
 */
export const expandGeoJsonPolygonToIncludePoints = (
	polygon: GeoJSONPolygon,
	points: [number, number][],
): GeoJSONPolygon | null => {
	const normalized = normalizeGeoJsonPolygon(polygon);
	if (!normalized) return null;

	const uniquePoints = dedupeCoordinates(points);
	const missingPoints = uniquePoints.filter(
		(point) => !isPointInGeoJsonPolygon(point, normalized),
	);
	if (missingPoints.length === 0) return normalized;

	const exterior = normalized.coordinates[0];
	if (!exterior) return null;

	const openRingVertices = exterior.slice(0, -1);
	let expandedVertices = openRingVertices;
	for (const point of missingPoints) {
		expandedVertices = insertPointBeyondNearestEdge(expandedVertices, point);
	}

	const insertedCandidate: GeoJSONPolygon = {
		coordinates: [closeRing(expandedVertices)],
		type: "Polygon",
	};

	if (
		isSimpleGeoJsonPolygon(insertedCandidate) &&
		polygonContainsAllPoints(insertedCandidate, uniquePoints)
	) {
		return insertedCandidate;
	}

	const hull = coordinatesToConvexHullPolygon(
		[...openRingVertices, ...missingPoints],
		{ paddingRatio: INCLUDE_POINTS_PADDING_RATIO },
	);
	const hullPolygon = hull ? featureToGeoJsonPolygon(hull) : null;
	if (hullPolygon && polygonContainsAllPoints(hullPolygon, uniquePoints)) {
		return hullPolygon;
	}

	const paddedHull = coordinatesToConvexHullPolygon(
		[...openRingVertices, ...missingPoints],
		{ paddingRatio: INCLUDE_POINTS_PADDING_RATIO * 3 },
	);
	return (
		(paddedHull ? featureToGeoJsonPolygon(paddedHull) : null) ?? normalized
	);
};

const toPolygonFeature = (ring: [number, number][]): Feature<Polygon> => ({
	geometry: {
		coordinates: [ring],
		type: "Polygon",
	},
	properties: {},
	type: "Feature",
});

const convexHullVertices = (
	coordinates: [number, number][],
): [number, number][] => {
	const points = dedupeCoordinates(coordinates);
	if (points.length <= 1) return points;

	const sorted = [...points].sort((left, right) =>
		left[0] === right[0] ? left[1] - right[1] : left[0] - right[0],
	);

	const lower: [number, number][] = [];
	for (const point of sorted) {
		while (
			lower.length >= 2 &&
			cross(lower[lower.length - 2]!, lower[lower.length - 1]!, point) <= 0
		) {
			lower.pop();
		}
		lower.push(point);
	}

	const upper: [number, number][] = [];
	for (let index = sorted.length - 1; index >= 0; index -= 1) {
		const point = sorted[index]!;
		while (
			upper.length >= 2 &&
			cross(upper[upper.length - 2]!, upper[upper.length - 1]!, point) <= 0
		) {
			upper.pop();
		}
		upper.push(point);
	}

	lower.pop();
	upper.pop();
	return lower.concat(upper);
};

/**
 * Builds a GeoJSON polygon rectangle that bounds the given `[lng, lat]` points.
 * Degenerate spans (single point or collinear sensors) are expanded to a minimum size.
 */
export const coordinatesToBoundingBoxPolygon = (
	coordinates: [number, number][],
	options: ZoneBoundsOptions = {},
): Feature<Polygon> | null => {
	if (coordinates.length === 0) return null;

	const minSpanDegrees = options.minSpanDegrees ?? DEFAULT_MIN_SPAN_DEGREES;
	const paddingRatio = options.paddingRatio ?? DEFAULT_PADDING_RATIO;

	let minLng = Number.POSITIVE_INFINITY;
	let maxLng = Number.NEGATIVE_INFINITY;
	let minLat = Number.POSITIVE_INFINITY;
	let maxLat = Number.NEGATIVE_INFINITY;

	for (const [lng, lat] of coordinates) {
		minLng = Math.min(minLng, lng);
		maxLng = Math.max(maxLng, lng);
		minLat = Math.min(minLat, lat);
		maxLat = Math.max(maxLat, lat);
	}

	const lngSpan = maxLng - minLng;
	const latSpan = maxLat - minLat;
	const effectiveLngSpan = Math.max(lngSpan, minSpanDegrees);
	const effectiveLatSpan = Math.max(latSpan, minSpanDegrees);
	const lngPad = effectiveLngSpan * paddingRatio;
	const latPad = effectiveLatSpan * paddingRatio;

	if (lngSpan === 0 && latSpan === 0) {
		const halfLng = effectiveLngSpan / 2 + lngPad;
		const halfLat = effectiveLatSpan / 2 + latPad;
		minLng -= halfLng;
		maxLng += halfLng;
		minLat -= halfLat;
		maxLat += halfLat;
	} else {
		minLng -= lngPad;
		maxLng += lngPad;
		minLat -= latPad;
		maxLat += latPad;
	}

	const ring: [number, number][] = [
		[minLng, minLat],
		[maxLng, minLat],
		[maxLng, maxLat],
		[minLng, maxLat],
		[minLng, minLat],
	];

	return toPolygonFeature(ring);
};

/**
 * Builds a convex hull polygon around the given points, with padding.
 * Falls back to a bounding box when fewer than three distinct points exist.
 */
export const coordinatesToConvexHullPolygon = (
	coordinates: [number, number][],
	options: ZoneBoundsOptions = {},
): Feature<Polygon> | null => {
	if (coordinates.length === 0) return null;

	const minSpanDegrees = options.minSpanDegrees ?? DEFAULT_MIN_SPAN_DEGREES;
	const paddingRatio = options.paddingRatio ?? DEFAULT_PADDING_RATIO;
	const uniquePoints = dedupeCoordinates(coordinates);

	if (uniquePoints.length < 3) {
		return coordinatesToBoundingBoxPolygon(coordinates, options);
	}

	const hullVertices = convexHullVertices(uniquePoints);
	if (hullVertices.length < 3) {
		return coordinatesToBoundingBoxPolygon(coordinates, options);
	}

	let ring = closeRing(hullVertices);
	if (paddingRatio > 0) {
		ring = expandRingFromCentroid(ring, paddingRatio);
	}

	const bounds = getOverlayBoundsFromRing(ring);
	const lngSpan = bounds.maxLng - bounds.minLng;
	const latSpan = bounds.maxLat - bounds.minLat;
	if (lngSpan < minSpanDegrees || latSpan < minSpanDegrees) {
		return coordinatesToBoundingBoxPolygon(coordinates, options);
	}

	return toPolygonFeature(ring);
};

const getOverlayBoundsFromRing = (
	ring: [number, number][],
): {
	maxLat: number;
	maxLng: number;
	minLat: number;
	minLng: number;
} => {
	const longitudes = ring.map(([lng]) => lng);
	const latitudes = ring.map(([, lat]) => lat);

	return {
		maxLat: Math.max(...latitudes),
		maxLng: Math.max(...longitudes),
		minLat: Math.min(...latitudes),
		minLng: Math.min(...longitudes),
	};
};

const buildDerivedZoneBoundsFeature = ({
	coordinates,
	options = {},
}: Omit<BuildZoneBoundsParams, "storedBoundary">): Feature<Polygon> | null => {
	const shape = options.shape ?? DEFAULT_ZONE_BOUNDS_SHAPE;

	if (shape === "markerConnect") {
		return coordinatesToMarkerConnectPolygon(coordinates, options);
	}

	if (shape === "convexHull") {
		return coordinatesToConvexHullPolygon(coordinates, options);
	}

	return coordinatesToBoundingBoxPolygon(coordinates, options);
};

/**
 * Builds a zone boundary polygon from sensor coordinates.
 */
export const buildZoneBoundsFeature = ({
	coordinates,
	fallbackToDerived = true,
	options = {},
	storedBoundary = null,
}: BuildZoneBoundsParams): Feature<LineString | Polygon> | null => {
	if (storedBoundary) {
		const normalizedLine = normalizeGeoJsonLineString(storedBoundary);
		if (normalizedLine) {
			return {
				...geoJsonLineStringToFeature(normalizedLine),
				properties: {
					boundsSource: "manual",
				},
			};
		}

		const normalizedBoundary = normalizeGeoJsonPolygon(storedBoundary);
		if (normalizedBoundary) {
			return {
				...geoJsonPolygonToFeature(normalizedBoundary),
				properties: {
					boundsSource: "manual",
				},
			};
		}
	}

	if (!fallbackToDerived) return null;

	return buildDerivedZoneBoundsFeature({ coordinates, options });
};

/** Shoelace area in lng/lat degrees² — stamped onto overlays for hit-rank stability.
 * When two zone fills overlap and you click the overlap, we want the smaller zone to win — this enables the concept of being able to pick/click the nested lobby (zone) instead of the whole building.
 */
export const ringAreaDegreesForHitRank = (ring: Position[]): number => {
	if (ring.length < 4) return 0;
	let area = 0;
	for (let index = 0; index < ring.length - 1; index += 1) {
		const [x1, y1] = ring[index]!;
		const [x2, y2] = ring[index + 1]!;
		area += (x1 ?? 0) * (y2 ?? 0) - (x2 ?? 0) * (y1 ?? 0);
	}
	return Math.abs(area) / 2;
};

export const toZoneBoundsOverlay = (
	zoneId: string,
	feature: Feature<LineString | Polygon>,
	shape: ZoneBoundsShape,
	zoneColor?: string,
	borderWidth?: number,
): Feature<LineString | Polygon> => {
	const hitRankArea =
		feature.geometry.type === "Polygon"
			? ringAreaDegreesForHitRank(feature.geometry.coordinates[0] ?? [])
			: 0;

	return {
		...feature,
		id: `zone-bounds:${zoneId}`,
		properties: {
			...feature.properties,
			boundsShape: shape,
			boundsSource: feature.properties?.boundsSource ?? "derived",
			hitRankArea,
			overlayStyle: feature.geometry.type === "LineString" ? "line" : "fill",
			zoneId,
			...(zoneColor?.trim() ? { zoneColor: zoneColor.trim() } : {}),
			...(typeof borderWidth === "number" && Number.isFinite(borderWidth)
				? { lineWidth: borderWidth }
				: {}),
		},
	};
};

/** Resolve zone id from a map overlay feature (`zone-bounds:` id or `zoneId` prop). */
export const getOverlayZoneId = (feature: Feature): string | undefined => {
	const id = feature.id;
	if (typeof id === "string" && id.startsWith("zone-bounds:")) {
		return id.slice("zone-bounds:".length);
	}

	const zoneId = feature.properties?.zoneId;
	return typeof zoneId === "string" ? zoneId : undefined;
};
